import { Input, Slider } from "web-design-system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface CalculatorInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  inputMin?: number;
  inputMax?: number;
  step?: number | ((value: number) => number);
  unit?: string;
  formatDisplay?: (value: number) => string;
}

export function CalculatorInput({
  label,
  value,
  onChange,
  min,
  max,
  inputMin,
  inputMax,
  step = 1,
  unit,
  formatDisplay,
}: CalculatorInputProps) {
  const [inputValue, setInputValue] = useState(String(value));
  const [isFocused, setIsFocused] = useState(false);
  const isFocusedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setInputValue(String(value));
    }
  }, [value]);

  const handleSliderChange = useCallback(
    ([val]: number[]) => {
      onChange(val);
    },
    [onChange]
  );

  const clamp = (val: number) => {
    const lo = inputMin ?? min;
    const hi = inputMax ?? max;
    return Math.min(hi, Math.max(lo, val));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);
    const parsed = Number(raw);
    if (!Number.isNaN(parsed) && raw !== "") {
      onChange(clamp(parsed));
    }
  };

  const handleInputBlur = () => {
    isFocusedRef.current = false;
    setIsFocused(false);
    const parsed = Number(inputValue);
    if (Number.isNaN(parsed) || inputValue === "") {
      setInputValue(String(value));
    } else {
      const clamped = clamp(parsed);
      setInputValue(String(clamped));
      onChange(clamped);
    }
  };

  const handleDisplayClick = () => {
    setIsFocused(true);
    isFocusedRef.current = true;
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const resolvedStep = useMemo(() => {
    if (typeof step === "function") return step(value);
    return step;
  }, [step, value]);

  const showFormatted = formatDisplay && !isFocused;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground shrink-0">{label}</span>
        <div className="flex items-center gap-2">
          {showFormatted ? (
            <div
              className="h-8 min-w-28 relative text-sm text-right flex items-center justify-end px-3 pr-7 cursor-text border border-input rounded-md"
              onClick={handleDisplayClick}
            >
              {formatDisplay(Number(inputValue))}
              {unit ? (
                <span className="absolute right-2.5 text-xs text-muted-foreground pointer-events-none">
                  {unit}
                </span>
              ) : null}
            </div>
          ) : (
            <div className="relative flex items-center">
              <Input
                ref={inputRef}
                type="number"
                className="h-8 w-28 text-sm text-right pr-7"
                value={inputValue}
                min={min}
                max={max}
                step={resolvedStep}
                onChange={handleInputChange}
                onFocus={() => {
                  isFocusedRef.current = true;
                  setIsFocused(true);
                }}
                onBlur={handleInputBlur}
                autoFocus={formatDisplay ? isFocused : undefined}
              />
              {unit ? (
                <span className="absolute right-2.5 text-xs text-muted-foreground pointer-events-none">
                  {unit}
                </span>
              ) : null}
            </div>
          )}
        </div>
      </div>
      <Slider
        min={min}
        max={max}
        step={resolvedStep}
        value={[value]}
        onValueChange={handleSliderChange}
      />
    </div>
  );
}
